import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { articles } from "@/seo/articles";
import { articlesEn } from "@/seo/articlesEn";
import { useLang, useTr } from "@/i18n";

export default function Blog() {
  const lang = useLang();
  const tr = useTr();
  const isEn = lang === "en";
  const list = isEn ? articlesEn : articles;
  const path = isEn ? "/en/blog" : "/blog";
  return (
    <>
      <Seo
        title={isEn ? "Blog — Wedding & Event DJ Guides | DJ Magix" : "Blog — Consigli per Eventi e Matrimoni | DJMAGIX"}
        description={isEn
          ? "Tips from a DJ with 25 years of experience: how to choose your wedding DJ, how much a DJ costs in Switzerland, and party playlists."
          : "Consigli da un DJ con 25 anni di esperienza: come scegliere il DJ per il matrimonio, quanto costa un DJ in Svizzera e le playlist per la festa."}
        path={path}
        lang={isEn ? "en" : "it"}
        alternates={isEn ? [
          { hreflang: "en", path: "/en/blog" },
          { hreflang: "it", path: "/blog" },
          { hreflang: "x-default", path: "/blog" },
        ] : [
          { hreflang: "it", path: "/blog" },
          { hreflang: "en", path: "/en/blog" },
          { hreflang: "x-default", path: "/blog" },
        ]}
      />
      <section className="pt-40 pb-16 text-center px-6">
        <motion.p
          className="text-xs uppercase tracking-[0.3em] text-[#BFA45D] font-medium"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {tr("blog.pageEyebrow", "Il Blog")}
        </motion.p>
        <motion.h1
          className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight gold-text mt-4"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {isEn ? "Advice from the DJ Booth" : "Consigli dal DJ Booth"}
        </motion.h1>
        <motion.p
          className="font-accent italic text-lg text-[#E2C882]/85 mt-4 max-w-2xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.25 }}
        >
          {tr("blog.pageAccent", "Guide pratiche per matrimoni, feste ed eventi — da chi le piste le vive da 25 anni.")}
        </motion.p>
      </section>

      <section className="pb-24 max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        {list.map((a, i) => (
          <motion.article
            key={a.slug}
            data-testid={`blog-card-${i}`}
            className="gold-frame rounded-xl bg-[#111111] overflow-hidden group flex flex-col"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: i * 0.12 }}
          >
            <div className="overflow-hidden">
              <img
                src={a.image}
                srcSet={`${a.image.replace(".webp", "-480.webp")} 480w, ${a.image.replace(".webp", "-768.webp")} 768w`}
                sizes="(max-width: 768px) 100vw, 33vw"
                alt={a.h1}
                width={800}
                height={384}
                loading="lazy"
                className="w-full h-48 object-cover saturate-[0.92] transition-all duration-700 group-hover:scale-105 group-hover:saturate-100"
              />
            </div>
            <div className="p-6 flex flex-col flex-1">
              <h2 className="font-display text-lg font-semibold text-[#F7DE8B] leading-snug">
                {a.h1}
              </h2>
              <p className="text-sm text-[#E2C882]/80 leading-relaxed mt-3 flex-1">
                {a.excerpt}
              </p>
              <Link
                to={`${isEn ? "/en/blog" : "/blog"}/${a.slug}`}
                data-testid={`blog-card-link-${i}`}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#E2B857] hover:text-[#F7DE8B] transition-colors mt-5"
              >
                {isEn ? "Read the article" : "Leggi l'articolo"} <ArrowRight size={14} />
              </Link>
            </div>
          </motion.article>
        ))}
        {!isEn && (
        <aside id="linea-editoriale" data-testid="editorial-policy" className="max-w-3xl mx-auto mt-14 gold-frame rounded-xl bg-[#111111] p-7">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#BFA45D]">Linea editoriale</p>
          <p className="text-sm text-[#E2C882]/80 leading-relaxed mt-3">
            Le guide di questo blog sono scritte da Luigi, in arte DJ Magix, e nascono da 25 anni
            di eventi reali in Svizzera — non dalla teoria. Nessun contenuto è sponsorizzato: i
            consigli su attrezzatura, prezzi e scalette riflettono esperienza diretta. Ogni
            articolo indica autore e data di pubblicazione. Hai trovato un errore o vuoi
            suggerire un tema? Scrivici: <a href="mailto:info@djmagix.net" className="text-[#E2B857] underline underline-offset-2">info@djmagix.net</a>.
          </p>
        </aside>
        )}
      </section>
    </>
  );
}
